import React, { useState, useEffect } from 'react';
import {
  getCategories,
  subscribeToCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  migrateFromMock,
} from '../../services/firestore';
import { services as mockServices } from '../../mock';
import {
  FolderTree,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
  Search,
  Database,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { toastSuccess, toastError, toastPromise } from '../../components/admin/ToastProvider';
import { useConfirm, ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { StatsCardSkeleton } from '../../components/admin/Skeleton';
import { CategoriesEmptyState, SearchEmptyState, ErrorState } from '../../components/admin/EmptyState';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Confirmation dialog hook
  const { confirm, ConfirmDialog } = useConfirm();

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  // Success states
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Migration states
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToCategories((data) => {
      setCategories(data);
      setFilteredCategories(data);
      setLoading(false);
      setError(null);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Filter categories based on search query
    if (searchQuery.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter((cat) =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (cat.description && cat.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredCategories(filtered);
    }
  }, [searchQuery, categories]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({ name: '', description: '', isActive: true });
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setModalMode('edit');
    setSelectedCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      isActive: category.isActive !== undefined ? category.isActive : true,
    });
    setError(null);
    setShowModal(true);
  };

  const openDeleteModal = async (category) => {
    const confirmed = await confirm(
      `Hapus kategori "${category.name}"? Tindakan tidak dapat dibatalkan.`,
      {
        title: 'Hapus Kategori',
        type: 'danger',
        confirmText: 'Ya, Hapus',
        cancelText: 'Batal',
      }
    );

    if (!confirmed) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await toastPromise(
        deleteCategory(category.id),
        {
          loading: 'Menghapus kategori...',
          success: 'Kategori berhasil dihapus!',
          error: 'Gagal menghapus kategori',
        }
      );
    } catch (error) {
      console.error('Error deleting category:', error);
      setError(error.message || 'Gagal menghapus kategori');
      toastError(error.message || 'Gagal menghapus kategori');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModals = () => {
    setShowModal(false);
    setShowDeleteModal(false);
    setSelectedCategory(null);
    setCategoryToDelete(null);
    setError(null);
    setSuccess(null);
  };

  const handleMigration = async () => {
    setIsMigrating(true);
    setMigrationResult(null);
    setError(null);

    try {
      const result = await migrateFromMock({ services: mockServices });
      setMigrationResult(result);
      setSuccess('Migrasi data berhasil!');

      setTimeout(() => {
        setMigrationResult(null);
        setSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Migration error:', error);
      setError('Gagal melakukan migrasi: ' + error.message);
    } finally {
      setIsMigrating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nama kategori harus diisi');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await toastPromise(
        modalMode === 'create'
          ? createCategory(formData)
          : updateCategory(selectedCategory.id, formData),
        {
          loading: modalMode === 'create' ? 'Menambahkan kategori...' : 'Memperbarui kategori...',
          success: modalMode === 'create' ? 'Kategori berhasil ditambahkan!' : 'Kategori berhasil diperbarui!',
          error: 'Gagal menyimpan kategori',
        }
      );
      closeModals();
    } catch (error) {
      console.error('Error saving category:', error);
      setError(error.message || 'Gagal menyimpan kategori');
      toastError(error.message || 'Gagal menyimpan kategori');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-2">
              <div className="h-8 w-48 bg-white/5 rounded animate-pulse" />
              <div className="h-4 w-64 bg-white/5 rounded animate-pulse" />
            </div>
            <div className="h-10 w-40 bg-white/5 rounded-lg animate-pulse" />
          </div>

          {/* Stats Skeleton */}
          <StatsCardSkeleton count={1} />

          {/* Search Skeleton */}
          <div className="h-12 bg-white/5 border border-[#D4AF7A]/20 rounded-lg animate-pulse" />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <ErrorState
          title="Gagal Memuat Data"
          message={error}
          onRetry={() => window.location.reload()}
        />
      )}

      {/* Content */}
      {!loading && !error && (
        <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#F7F5F2]">Kategori Layanan</h2>
          <p className="text-[#F7F5F2]/70">Kelola kategori layanan henna</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus size={20} />
          Tambah Kategori
        </button>
      </div>

      {/* Migration Banner */}
      {categories.length === 0 && !loading && (
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Database className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-400 mb-2">
                Inisialisasi Data
              </h3>
              <p className="text-[#F7F5F2]/80 mb-4">
                Belum ada data kategori. Import data dari mock data untuk memulai?
              </p>

              {/* Migration Result */}
              {migrationResult && (
                <div className="mb-4 p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="font-semibold text-green-400">Migrasi Berhasil!</span>
                  </div>
                  <div className="text-sm text-[#F7F5F2]/80 space-y-1">
                    <p>✓ {migrationResult.categories.created} kategori dibuat</p>
                    <p>✓ {migrationResult.packages.created} paket dibuat</p>
                    {migrationResult.categories.errors.length > 0 && (
                      <p className="text-yellow-400">
                        ⚠ {migrationResult.categories.errors.length} kategori gagal
                      </p>
                    )}
                    {migrationResult.packages.errors.length > 0 && (
                      <p className="text-yellow-400">
                        ⚠ {migrationResult.packages.errors.length} paket gagal
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleMigration}
                  disabled={isMigrating}
                  className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isMigrating ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <Database size={16} />
                      Import Data dari Mock
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats & Search */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Total Kategori</p>
              <p className="text-2xl font-bold text-[#F7F5F2]">{categories.length}</p>
            </div>
            <FolderTree className="w-8 h-8 text-[#D4AF7A]" />
          </div>
        </div>

        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F7F5F2]/50" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Cari kategori..."
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
            />
          </div>
        </div>
      </div>

      {/* Categories List */}
      {filteredCategories.length === 0 ? (
        searchQuery ? (
          <SearchEmptyState
            onClear={() => {
              setSearchQuery('');
            }}
          />
        ) : (
          <CategoriesEmptyState
            onAdd={() => openCreateModal()}
          />
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCategories.map((category) => (
            <div
              key={category.id}
              className={`bg-white/5 border rounded-xl p-6 transition-all ${
                category.isActive === false
                  ? 'border-red-500/30 opacity-70'
                  : 'border-[#D4AF7A]/20'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-[#F7F5F2]">
                  {category.name}
                </h3>
                {category.isActive === false && (
                  <span className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                    Non-aktif
                  </span>
                )}
              </div>
              <p className="text-sm text-[#F7F5F2]/60 mb-4 line-clamp-2">
                {category.description || 'Tidak ada deskripsi'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(category)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors text-sm"
                >
                  <Edit2 size={14} />
                  Edit
                </button>
                <button
                  onClick={() => openDeleteModal(category)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                >
                  <Trash2 size={14} />
                  Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#D4AF7A]/20">
              <h3 className="text-lg font-semibold text-[#F7F5F2]">
                {modalMode === 'create' ? 'Tambah Kategori Baru' : 'Edit Kategori'}
              </h3>
              <button
                onClick={closeModals}
                className="p-1 text-[#F7F5F2]/70 hover:text-[#F7F5F2] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-green-400">{success}</p>
                </div>
              )}

              {/* Name Input */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                  Nama Kategori <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Contoh: Wedding, Engagement, dll"
                  className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  required
                />
              </div>

              {/* Description Input */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                  Deskripsi
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Deskripsi kategori..."
                  rows={3}
                  className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded border-[#D4AF7A]/30 text-[#D4AF7A] focus:ring-[#D4AF7A] focus:ring-offset-0 bg-white/5"
                />
                <label htmlFor="isActive" className="text-sm text-[#F7F5F2]">
                  Kategori aktif
                </label>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-2 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                  disabled={isSubmitting}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Menyimpan...
                    </span>
                  ) : modalMode === 'create' ? (
                    'Tambah'
                  ) : (
                    'Simpan'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminCategories;
